import { useEffect, useState } from 'react';
import api from '../../api/axios';
import RichEditor from '../components/RichEditor';

const FILTERS = [
  { value: 'all',      label: 'All members',         desc: 'Everyone with an email on record' },
  { value: 'paid',     label: 'Active paid members',  desc: 'Paid & not expired' },
  { value: 'pending',  label: 'Pending / expired',    desc: 'Unpaid or expired membership' },
  { value: 'selected', label: 'Selected members',     desc: 'Choose specific members below' },
];

export default function BroadcastManager() {
  const [subject, setSubject]           = useState('');
  const [body, setBody]                 = useState('');
  const [formKey, setFormKey]           = useState(0);
  const [filter, setFilter]             = useState('all');
  const [documents, setDocuments]       = useState([]);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [members, setMembers]           = useState([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [pickedIds, setPickedIds]       = useState(new Set());
  const [sending, setSending]           = useState(false);
  const [result, setResult]             = useState(null);
  const [error, setError]               = useState('');

  const bodyText = body.replace(/<[^>]+>/g, '').trim();

  useEffect(() => {
    api.get('/documents').then(r => setDocuments(r.data || [])).catch(() => {});
    api.get('/members').then(r => setMembers(r.data || [])).catch(() => {});
  }, []);

  const recipientCount = () => {
    if (filter === 'selected') return pickedIds.size;
    let t = members.filter(m => m.email);
    if (filter === 'paid')    t = t.filter(m => m.payment_status === 'paid' && m.expires_at && new Date(m.expires_at) > new Date());
    if (filter === 'pending') t = t.filter(m => m.payment_status !== 'paid' || !m.expires_at || new Date(m.expires_at) <= new Date());
    return t.length;
  };

  const toggleDoc  = (id) => setSelectedDocs(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const togglePick = (id) => setPickedIds(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const visibleMembers = members.filter(m => m.email && (
    !memberSearch || `${m.first_name} ${m.last_name} ${m.email}`.toLowerCase().includes(memberSearch.toLowerCase())
  ));

  const allVisiblePicked = visibleMembers.length > 0 && visibleMembers.every(m => pickedIds.has(m.id));
  const toggleAllVisible = () => {
    if (allVisiblePicked) {
      setPickedIds(s => { const n = new Set(s); visibleMembers.forEach(m => n.delete(m.id)); return n; });
    } else {
      setPickedIds(s => { const n = new Set(s); visibleMembers.forEach(m => n.add(m.id)); return n; });
    }
  };

  const send = async () => {
    if (!subject.trim() || !bodyText) { setError('Subject and body are required.'); return; }
    const count = recipientCount();
    if (count === 0) { setError('No recipients selected.'); return; }
    if (!window.confirm(`Send this email to ${count} member${count !== 1 ? 's' : ''}?`)) return;
    setSending(true); setError(''); setResult(null);
    try {
      const payload = { subject, body, recipientFilter: filter, documentIds: selectedDocs };
      if (filter === 'selected') payload.memberIds = [...pickedIds];
      const { data } = await api.post('/broadcast', payload);
      setResult(data);
      setSubject(''); setBody(''); setSelectedDocs([]); setPickedIds(new Set()); setFormKey(k => k + 1);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send broadcast');
    } finally { setSending(false); }
  };

  const reset = () => { setResult(null); setError(''); };

  if (result) {
    return (
      <div>
        <h4 className="fw-bold mb-4">Email Members</h4>
        <div className="bg-white rounded shadow-sm p-5 text-center" style={{ maxWidth: 500, margin: '0 auto' }}>
          <i className="fas fa-check-circle fa-4x text-success mb-3 d-block"></i>
          <h5 className="fw-bold mb-1">Broadcast Sent!</h5>
          <p className="text-muted mb-4">
            <strong className="text-success">{result.sent}</strong> emails sent successfully
            {result.failed > 0 && <>, <strong className="text-danger">{result.failed}</strong> failed</>}.
          </p>
          <button className="btn btn-primary rounded-pill px-5" onClick={reset}>
            <i className="fas fa-plus me-2"></i>Send Another
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-bold mb-0">Email Members</h4>
          <p className="text-muted small mb-0">Compose and send an email to your membership</p>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2 mb-3">{error}</div>}

      <div className="row g-4">
        {/* Compose panel */}
        <div className="col-lg-7">
          <div className="bg-white rounded shadow-sm p-4">
            <h6 className="fw-bold mb-3 text-muted text-uppercase small">Compose</h6>

            {/* Recipients */}
            <div className="mb-4">
              <label className="form-label small fw-bold">Recipients</label>
              <div className="d-flex flex-column gap-2">
                {FILTERS.map(f => (
                  <label key={f.value} className={`d-flex align-items-center gap-3 p-3 rounded border ${filter === f.value ? 'border-primary bg-primary bg-opacity-10' : 'border-light'}`} style={{ cursor: 'pointer' }}>
                    <input type="radio" name="filter" value={f.value} checked={filter === f.value} onChange={() => setFilter(f.value)} />
                    <div>
                      <div className="fw-semibold small">{f.label}</div>
                      <div className="text-muted" style={{ fontSize: '0.75rem' }}>{f.desc}</div>
                    </div>
                    {filter === f.value && (
                      <span className="ms-auto badge bg-primary">{recipientCount()} recipient{recipientCount() !== 1 ? 's' : ''}</span>
                    )}
                  </label>
                ))}
              </div>

              {/* Member picker — shown only when "selected" is active */}
              {filter === 'selected' && (
                <div className="mt-3 border rounded" style={{ overflow: 'hidden' }}>
                  <div className="p-2 border-bottom bg-light d-flex align-items-center gap-2">
                    <input
                      className="form-control form-control-sm"
                      placeholder="Search members…"
                      value={memberSearch}
                      onChange={e => setMemberSearch(e.target.value)}
                    />
                    <label className="d-flex align-items-center gap-2 text-nowrap small text-muted px-1" style={{ cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        className="form-check-input mt-0"
                        checked={allVisiblePicked}
                        onChange={toggleAllVisible}
                      />
                      All
                    </label>
                  </div>
                  <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                    {members.length === 0 ? (
                      <div className="text-center text-muted small py-4">Loading members…</div>
                    ) : visibleMembers.length === 0 ? (
                      <div className="text-center text-muted small py-4">No members match</div>
                    ) : visibleMembers.map(m => (
                      <label
                        key={m.id}
                        className={`d-flex align-items-center gap-3 px-3 py-2 border-bottom ${pickedIds.has(m.id) ? 'bg-primary bg-opacity-10' : ''}`}
                        style={{ cursor: 'pointer', margin: 0 }}
                      >
                        <input
                          type="checkbox"
                          className="form-check-input mt-0 flex-shrink-0"
                          checked={pickedIds.has(m.id)}
                          onChange={() => togglePick(m.id)}
                        />
                        <div className="flex-grow-1 min-width-0">
                          <div className="fw-semibold small text-truncate">{m.first_name} {m.last_name}</div>
                          <div className="text-muted text-truncate" style={{ fontSize: '0.72rem' }}>{m.email}</div>
                        </div>
                        <span className={`badge flex-shrink-0 ${m.payment_status === 'paid' ? 'badge-soft-success' : 'badge-soft-warning'}`} style={{ fontSize: '0.65rem' }}>
                          {m.payment_status === 'paid' ? 'Paid' : 'Pending'}
                        </span>
                      </label>
                    ))}
                  </div>
                  {pickedIds.size > 0 && (
                    <div className="px-3 py-2 bg-light border-top d-flex align-items-center justify-content-between">
                      <span className="small text-primary fw-semibold">
                        <i className="fas fa-user-check me-1"></i>{pickedIds.size} member{pickedIds.size !== 1 ? 's' : ''} selected
                      </span>
                      <button type="button" className="btn btn-sm btn-link text-muted p-0" onClick={() => setPickedIds(new Set())}>
                        Clear all
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Subject */}
            <div className="mb-3">
              <label className="form-label small fw-bold">Subject *</label>
              <input className="form-control" placeholder="Email subject line…" value={subject} onChange={e => { setSubject(e.target.value); setError(''); }} />
            </div>

            {/* Body */}
            <div className="mb-3">
              <label className="form-label small fw-bold">Message Body *</label>
              <RichEditor
                key={formKey}
                defaultValue={body}
                onChange={html => { setBody(html); setError(''); }}
                placeholder="Write your message here…"
                minHeight={220}
              />
              <div className="text-muted mt-1" style={{ fontSize: '0.75rem' }}>
                The recipient's name will be added automatically at the top of the email.
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar: Documents + Send */}
        <div className="col-lg-5">
          {/* Document picker */}
          <div className="bg-white rounded shadow-sm p-4 mb-3">
            <h6 className="fw-bold mb-1 text-muted text-uppercase small">Attach Documents</h6>
            <p className="text-muted mb-3" style={{ fontSize: '0.78rem' }}>Selected documents will appear as download links in the email.</p>
            {documents.length === 0 ? (
              <div className="text-muted small text-center py-3">
                <i className="fas fa-file-alt fa-2x d-block mb-2 opacity-25"></i>
                No documents uploaded yet
              </div>
            ) : (
              <div className="d-flex flex-column gap-2" style={{ maxHeight: 300, overflowY: 'auto' }}>
                {documents.map(d => (
                  <label key={d.id} className={`d-flex align-items-center gap-3 p-2 rounded border ${selectedDocs.includes(d.id) ? 'border-primary bg-primary bg-opacity-10' : 'border-light'}`} style={{ cursor: 'pointer' }}>
                    <input type="checkbox" checked={selectedDocs.includes(d.id)} onChange={() => toggleDoc(d.id)} />
                    <div className="flex-grow-1 min-width-0">
                      <div className="fw-semibold small text-truncate">{d.title}</div>
                      {d.file_type && <div className="text-muted" style={{ fontSize: '0.7rem' }}>{d.file_type} · {d.category}</div>}
                    </div>
                    {selectedDocs.includes(d.id) && <i className="fas fa-check-circle text-primary flex-shrink-0"></i>}
                  </label>
                ))}
              </div>
            )}
            {selectedDocs.length > 0 && (
              <div className="mt-2 small text-primary fw-semibold">
                <i className="fas fa-paperclip me-1"></i>{selectedDocs.length} document{selectedDocs.length > 1 ? 's' : ''} selected
              </div>
            )}
          </div>

          {/* Send summary */}
          <div className="bg-white rounded shadow-sm p-4">
            <h6 className="fw-bold mb-3 text-muted text-uppercase small">Send Summary</h6>
            <div className="d-flex flex-column gap-2 mb-4">
              <div className="d-flex justify-content-between small">
                <span className="text-muted">Recipients</span>
                <strong>{recipientCount()} member{recipientCount() !== 1 ? 's' : ''}</strong>
              </div>
              <div className="d-flex justify-content-between small">
                <span className="text-muted">Subject</span>
                <span className="text-truncate ms-3" style={{ maxWidth: 160 }}>{subject || <em className="text-muted">—</em>}</span>
              </div>
              <div className="d-flex justify-content-between small">
                <span className="text-muted">Documents</span>
                <strong>{selectedDocs.length > 0 ? `${selectedDocs.length} attached` : 'None'}</strong>
              </div>
            </div>

            <button className="btn btn-primary w-100 rounded-pill" onClick={send}
              disabled={sending || !subject.trim() || !bodyText || recipientCount() === 0}>
              {sending
                ? <><span className="spinner-border spinner-border-sm me-2"></span>Sending to {recipientCount()} member{recipientCount() !== 1 ? 's' : ''}…</>
                : <><i className="fas fa-paper-plane me-2"></i>Send Email to {recipientCount()} Member{recipientCount() !== 1 ? 's' : ''}</>}
            </button>
            {recipientCount() === 0 && (
              <p className="text-muted text-center small mt-2">
                {filter === 'selected' ? 'Select at least one member above.' : 'No recipients match the selected filter.'}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
